import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Send,
  Users,
  Library,
  PieChart,
  CreditCard,
  Blocks,
  KeyRound,
  BookOpen,
  LogOut,
} from 'lucide-react';
import { useState, type ComponentType } from 'react';
import { useAuth } from '../auth/AuthContext';
import { OrgSwitcher } from './OrgSwitcher';
import { UsersPanel } from './UsersPanel';
import { cx } from './ui';

// Event Log lives under Reporting; Usage lives under Billing (tabs) — not top-level nav items.
const NAV: {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/campaigns', label: 'Campaigns', icon: Send },
  { to: '/people', label: 'People', icon: Users },
  { to: '/library', label: 'Library', icon: Library },
  { to: '/reporting', label: 'Reporting', icon: PieChart },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/integrations', label: 'Integrations', icon: Blocks },
  { to: '/api-keys', label: 'API / MCP', icon: KeyRound },
  { to: '/docs', label: 'API Docs', icon: BookOpen },
];

function initials(email?: string): string {
  return (email ?? '?').slice(0, 2).toUpperCase();
}

export function Layout() {
  const { user, logout } = useAuth();
  const [usersOpen, setUsersOpen] = useState(false);
  return (
    <div className="flex h-screen bg-paper text-ink">
      <aside className="flex w-60 flex-col border-r border-line bg-paper">
        <div className="px-6 pb-4 pt-6">
          <span className="font-display text-2xl font-medium tracking-tight text-ink">Pavlov.</span>
        </div>

        <OrgSwitcher />

        <nav className="mt-4 flex-1 space-y-0.5 px-3">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-ink/[0.04] font-medium text-ink'
                    : 'text-muted hover:bg-ink/[0.03] hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cx(
                      'h-[18px] w-[18px] shrink-0',
                      isActive ? 'text-ink' : 'text-muted group-hover:text-ink',
                    )}
                  />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setUsersOpen(true)}
              title="Team & roles"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-ink/[0.04]"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line text-xs font-medium text-muted">
                {initials(user?.email)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">
                  {user?.name ?? 'Operator'}
                </div>
                <div className="truncate text-xs text-muted">{user?.email}</div>
              </div>
            </button>
            <button
              onClick={() => void logout()}
              title="Sign out"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {usersOpen && <UsersPanel onClose={() => setUsersOpen(false)} currentEmail={user?.email} />}

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-content animate-fade-in px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
